import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import { useAuth } from '@/lib/auth/auth-provider';
import type { MfaSetupError } from '@/lib/auth/errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

import { OtpCodeInput } from './OtpCodeInput';

const CODE_LENGTH = 6;
/** Shown in Cognito's device list. Cognito limits this to alphanumeric + a few specials. */
const FRIENDLY_DEVICE_NAME = 'Mailchute.ai authenticator';

/**
 * `/auth/mfa-setup` — TOTP enrolment for a signed-in user.
 *
 * On mount, calls `AssociateSoftwareTokenCommand` to get a secret. Renders
 * it as a QR code plus a human-readable fallback. The user scans with
 * their authenticator app (Authy, 1Password, Google Authenticator), types
 * the 6-digit code their app generates, and we call `VerifySoftwareToken`
 * + `SetUserMFAPreference` to enable it as their preferred MFA method.
 *
 * For now the page is reachable by direct URL (no settings page yet to
 * link from). Per ADR 0004 / CLAUDE.md §10.1.2 this is custom UI top to
 * bottom — no Cognito Hosted UI.
 */
export function MfaSetupPage() {
  const navigate = useNavigate();
  const { setupTotp, verifyTotpAndEnable } = useAuth();

  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // The associate call has to fire exactly once. Without a guard, React 19
  // StrictMode's double-invoke would surface a second secret and confuse
  // the user mid-scan. The ref is set inside the effect (not at module
  // scope) so test renders get a fresh one.
  const associateFiredRef = useRef(false);

  useEffect(() => {
    if (associateFiredRef.current) return;
    associateFiredRef.current = true;
    void setupTotp().then((result) => {
      if (result.isOk()) {
        setSecretCode(result.value.secretCode);
        setOtpauthUri(result.value.otpauthUri);
      } else {
        setSetupError(mfaSetupErrorMessage(result.error));
      }
    });
  }, [setupTotp]);

  const submit = async (codeToSubmit: string) => {
    if (codeToSubmit.length !== CODE_LENGTH || isVerifying) return;
    setVerifyError(null);
    setIsVerifying(true);
    try {
      const result = await verifyTotpAndEnable(codeToSubmit, FRIENDLY_DEVICE_NAME);
      if (result.isOk()) {
        toast.success('Two-factor authentication is on. Welcome to Mailchute.ai.');
        void navigate('/app');
        return;
      }
      setVerifyError(mfaSetupErrorMessage(result.error));
    } finally {
      setIsVerifying(false);
    }
  };

  const canSubmit = code.length === CODE_LENGTH && !isVerifying && secretCode !== null;

  return (
    <main className="bg-muted/30 flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <CardTitle className="text-xl">Set up two-factor authentication</CardTitle>
          <CardDescription>
            Scan the QR code with your authenticator app, then enter the code it generates.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {setupError ? (
            <Alert variant="destructive">
              <AlertDescription>{setupError}</AlertDescription>
            </Alert>
          ) : null}

          {otpauthUri && secretCode ? (
            <>
              <div className="flex flex-col items-center gap-3">
                <div className="border-border rounded-md border bg-white p-3">
                  <QRCodeSVG value={otpauthUri} size={176} level="M" marginSize={0} />
                </div>
                <details className="text-muted-foreground text-xs">
                  <summary className="hover:text-foreground cursor-pointer">
                    Can&apos;t scan? Enter the code manually
                  </summary>
                  <p className="text-foreground mt-2 break-all font-mono">
                    {formatSecret(secretCode)}
                  </p>
                </details>
              </div>

              {verifyError ? (
                <Alert variant="destructive">
                  <AlertDescription>{verifyError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="mfa-setup-code-0">Verification code</Label>
                <OtpCodeInput
                  id="mfa-setup-code-0"
                  value={code}
                  onChange={(next) => {
                    setCode(next);
                    if (next.length === CODE_LENGTH) {
                      void submit(next);
                    }
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: page mounts directly after the user clicks "Set up MFA"; QR + secret are visual reference, the code field is the action
                  autoFocus={false}
                  describedBy="mfa-setup-hint"
                />
                <p id="mfa-setup-hint" className="text-muted-foreground text-xs">
                  Codes refresh every 30 seconds.
                </p>
              </div>

              <Button
                type="button"
                className="w-full"
                disabled={!canSubmit}
                onClick={() => void submit(code)}
              >
                {isVerifying ? 'Verifying…' : 'Verify and finish'}
              </Button>
            </>
          ) : null}

          {!otpauthUri && !setupError ? (
            <p className="text-muted-foreground text-center text-sm">
              Loading your authenticator secret…
            </p>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}

/**
 * Cognito returns the secret as a single base32 string. Splitting it into
 * 4-character groups makes manual entry massively less error-prone.
 */
function formatSecret(raw: string): string {
  return raw.match(/.{1,4}/g)?.join(' ') ?? raw;
}

function mfaSetupErrorMessage(error: MfaSetupError): string {
  switch (error.kind) {
    case 'code-mismatch':
      return 'That code is incorrect. Check your authenticator app and try again.';
    case 'invalid-code-format':
      return 'Verification codes are six digits.';
    case 'too-many-requests':
      return 'Too many attempts. Please wait a minute before trying again.';
    case 'session-expired':
      return 'Your session expired. Please sign in again before setting up MFA.';
    case 'unknown':
      return error.message;
  }
}
